'use client';

import React, {
  createContext,
  PropsWithChildren,
  useMemo,
  useEffect,
  useCallback,
  useState,
} from 'react';
import * as signalR from '@microsoft/signalr';
import {
  HubConnection,
  IHttpConnectionOptions,
  LogLevel,
} from '@microsoft/signalr';
import {
  getAuthToken,
  removeSignalRToken,
} from '@/utils/auth';
import {
  baseApiUrl,
} from '@/env';
import {
  signalRConfig,
} from '@/config/signalRConfig';

type SignalRHubContextProps = {
  hubConnection: HubConnection | null;
  reconnect: (token?: string | null) => Promise<HubConnection | null>;
  disconnect: () => Promise<void>;
};

export const SignalRHubContext = createContext<SignalRHubContextProps>({
  hubConnection: null,
  reconnect: async () => null,
  disconnect: async () => {},
});

const getHubOptions = (): IHttpConnectionOptions => {
  // Prefer a SignalR-specific token if present, otherwise fallback to auth token
  const signalROverrideToken = typeof window !== 'undefined'
    ? window.localStorage.getItem('signalr_token')
    : null;
  const token = signalROverrideToken ?? getAuthToken();

  const options: IHttpConnectionOptions = {
    withCredentials: false,
    skipNegotiation: false,
    transport: signalR.HttpTransportType.WebSockets,
    accessTokenFactory(): string {
      return token ?? '';
    },
  };

  options.headers = {
    ...(options.headers ?? {}),
    Authorization: token ? `Bearer ${token}` : undefined,
  } as Record<string, string>;

  return options;
};

export function getNewHubConnection(): HubConnection {
  const endpoint = `${baseApiUrl}/${signalRConfig.signalRPrefix}/logins`;

  const options: IHttpConnectionOptions = getHubOptions();
  const loggerType = signalRConfig.isLogEnabled
    ? LogLevel.Information
    : LogLevel.Error;

  return new signalR
    .HubConnectionBuilder()
    .withUrl(endpoint, options)
    .withAutomaticReconnect(signalRConfig.retry)
    .configureLogging(loggerType)
    .build();
}

export default function SignalRHubProvider({
  children,
}: PropsWithChildren) {
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(null);
  const hasSignalRToken = (): boolean => {
    if (typeof window === 'undefined') return false;
    const signalRToken = window.localStorage.getItem('signalr_token');
    return !!signalRToken;
  };

  const createConnection = async (): Promise<HubConnection | null> => {
    try {
      const connection = getNewHubConnection();
      await connection.start();
      setHubConnection(connection);
      return connection;
    } catch (error) {
      console.error('Error creating SignalR connection', error);
      return null;
    }
  };

  const resetHubConnection = useCallback(async () => {
    try {
      await hubConnection?.stop();
    } finally {
      setHubConnection(null);
    }
  }, [hubConnection]);

  const disconnect = useCallback(async () => {
    try {
      await hubConnection?.stop();
    } catch (error) {
      console.error('Error disconnecting SignalR:', error);
    } finally {
      setHubConnection(null);
      removeSignalRToken();
    }
  }, [hubConnection]);

  useEffect(() => {
    if (hasSignalRToken()) {
      createConnection();
    }
    return () => {
      resetHubConnection();
    };
  }, []);

  const reconnect = useCallback(async (token?: string | null): Promise<HubConnection | null> => {
    try {
      if (typeof window !== 'undefined') {
        if (token && token.length > 0) {
          window.localStorage.setItem('signalr_token', token);
        }
      }
      await resetHubConnection();
      const connection = await createConnection();
      return connection;
    } catch (error) {
      console.error('Error reconnecting SignalR', error);
      return null;
    }
  }, [resetHubConnection]);

  const value = useMemo(() => ({
    hubConnection,
    reconnect,
    disconnect,
  }), [hubConnection, reconnect, disconnect]);

  return (
    <SignalRHubContext.Provider value={value}>
      {children}
    </SignalRHubContext.Provider>
  );
}
